/**
 * <blindly-cart-upsell> — native in-cart "add another book and save" widget.
 *
 * Defined as a custom element (loaded once, globally) because the mini-cart
 * drawer is injected and re-rendered via innerHTML, where inline <script> tags
 * never execute. connectedCallback re-runs on every drawer re-render, so each
 * fresh instance binds its own handlers — no global init guard needed.
 *
 * All tier / price / offer logic is computed SERVER-SIDE in the Liquid snippet
 * (recomputed on every render). This file only handles: add-to-cart (with the
 * correct tier Loop plan + line item properties), the flavor picker modal, and
 * carousel scrolling.
 */
class BlindlyCartUpsell extends HTMLElement {
  connectedCallback() {
    this.SECTIONS = ['mini-cart', 'cart-icon-bubble', 'mobile-cart-icon-bubble'];
    this.mode = this.dataset.bcuMode;          // 'sub' | 'otp'
    this.nextPlan = this.dataset.bcuNextPlan;   // tier-N Loop plan id (sub only)
    this.pending = null;

    this.flavors = {};
    try {
      const raw = this.querySelector('[data-bcu-flavors]');
      this.flavors = raw ? (JSON.parse(raw.textContent || '{}') || {}) : {};
    } catch (e) {
      console.warn('[bcu] flavor map JSON invalid', e);
    }

    this.bindCards();
    this.bindFlavorModal();
    this.bindWildModal();
    this.bindArrows();
  }

  flavorsFor(title) {
    const list = this.flavors[title];
    return Array.isArray(list) ? list : [];
  }

  // ── Add to cart + re-render the drawer ──────────────────────────
  addBook(variantId, title, flavor, btn, readingPrefs) {
    // Busy lock: ignore further clicks (incl. other cards) until this add
    // resolves, so two concurrent /cart/add calls can't race the re-render.
    if (BlindlyCartUpsell.busy) return;
    BlindlyCartUpsell.busy = true;
    this.setButtonsDisabled(true);
    if (btn) btn.classList.add('is-loading');

    const props = {
      _bundle_role: 'book',
      _bundle_kind: 'cart_addon',
      _add_source: 'cart_upsell',
    };
    if (flavor) props._bundle_flavor = flavor;
    // Wild Card carries the reader's exclusions instead of a flavor: it is the
    // one card where they pick no genre, so "what not to send" is the only
    // preference they can express. Same property names the quiz writes and Loop
    // persists onto renewals, so Athena's picker already reads them.
    if (readingPrefs) {
      if (readingPrefs.genres && readingPrefs.genres.length) {
        props._reading_skip_genres = readingPrefs.genres.join(', ');
      }
      if (readingPrefs.themes && readingPrefs.themes.length) {
        props._reading_skip_themes = readingPrefs.themes.join(', ');
      }
    }

    const body = {
      id: variantId,
      quantity: 1,
      properties: props,
      sections: this.SECTIONS,
      sections_url: window.location.pathname,
    };
    if (this.mode === 'sub' && this.nextPlan) body.selling_plan = this.nextPlan;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    })
      .then((r) => {
        if (!r.ok) return r.json().then((j) => { throw new Error(j.description || j.message || 'Cart error'); });
        return r.json();
      })
      .then((state) => {
        // The book IS in the cart now. From here, never surface an "add
        // failed" message — any failure is a *render* failure, which we
        // recover from by re-fetching the drawer (not by lying to the user).
        try {
          if (typeof window.blindlyTrack === 'function') {
            window.blindlyTrack('bcu_book_added', { variant_id: variantId, flavor: flavor || null, mode: this.mode });
          }
        } catch (e) { /* noop */ }
        this.refreshDrawer(state);
      })
      .catch((err) => {
        // Only reached when the ADD itself failed (network / !ok / sold out).
        console.error('[bcu] add failed', err);
        try { if (typeof window.blindlyTrack === 'function') window.blindlyTrack('bcu_add_failed', { variant_id: variantId, mode: this.mode, message: String(err && err.message || err).slice(0, 200) }); } catch (e) { /* noop */ }
        BlindlyCartUpsell.busy = false;
        this.setButtonsDisabled(false);
        if (btn) btn.classList.remove('is-loading');
        window.alert('Sorry — we couldn’t add that. Please try again.');
      });
  }

  setButtonsDisabled(disabled) {
    this.querySelectorAll('[data-bcu-add]').forEach((b) => { b.disabled = disabled; });
  }

  // Update the drawer from the add response. Subscription adds can return
  // sections:null (a known Loop/Shopify hiccup) — in that case renderContents
  // would throw `Cannot read properties of null`, so we guard and fall back to
  // a soft re-fetch of the drawer section instead of a hard error / reload.
  refreshDrawer(state) {
    const miniCart = document.querySelector('mini-cart');

    // Keep the rest of the cart ecosystem (count bubble, gift reconciler) in sync.
    if (window.publish && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      try {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'blindly-cart-upsell', cartData: state });
      } catch (e) { /* noop */ }
    }

    const hasSections = state && state.sections && state.sections['mini-cart'];
    let recoverReason = hasSections ? null : (state && state.sections === null ? 'sections_null' : 'sections_missing');
    if (miniCart && typeof miniCart.renderContents === 'function' && hasSections) {
      try {
        miniCart.renderContents(state);
        BlindlyCartUpsell.busy = false; // a fresh instance re-binds on render
        return;
      } catch (e) {
        console.warn('[bcu] renderContents failed; re-fetching drawer', e);
        recoverReason = 'render_error';
      }
    }
    // The add succeeded but the drawer could not repaint from the response.
    // This is the class of failure that hides behind a working cart.
    try { if (typeof window.blindlyTrack === 'function') window.blindlyTrack('cart_drawer_render_recovered', { reason: recoverReason, mode: this.mode, source: 'blindly-cart-upsell' }); } catch (e) { /* noop */ }
    this.refetchDrawer(miniCart);
  }

  // Soft recovery: pull the drawer section fresh (mirrors MiniCart's own lazy
  // load) so the UI reflects the real cart without a jarring full reload.
  refetchDrawer(miniCart) {
    const url = miniCart && miniCart.dataset ? miniCart.dataset.url : null;
    const target = document.getElementById('mini-cart');
    const hardReload = (why) => {
      try { if (typeof window.blindlyTrack === 'function') window.blindlyTrack('cart_drawer_hard_reload', { reason: why, source: 'blindly-cart-upsell' }); } catch (e) { /* noop */ }
      setTimeout(() => window.location.reload(), 150); // let the beacon leave first
    };
    if (!url || !target) {
      hardReload('no_drawer_url');
      return;
    }
    fetch(url)
      .then((r) => r.text())
      .then((html) => {
        const section = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
        if (!section) { hardReload('refetch_no_section'); return; }
        target.innerHTML = section.innerHTML;
        document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
        BlindlyCartUpsell.busy = false;
      })
      .catch(() => hardReload('refetch_failed'));
  }

  // ── Card add buttons ────────────────────────────────────────────
  bindCards() {
    this.querySelectorAll('[data-bcu-add]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const variantId = btn.dataset.variantId;
        const title = btn.dataset.title || '';
        const flavors = this.flavorsFor(title);
        if (flavors.length) {
          this.openFlavorModal(variantId, title, flavors, btn);
        } else {
          this.addBook(variantId, title, null, btn);
        }
      });
    });
    // The "i" trigger on the Wild Card cover also opens its info modal.
    this.querySelectorAll('[data-bcu-wild-info]').forEach((el) => {
      el.addEventListener('click', () => this.openWildModal());
    });
  }

  // ── Wild Card info modal ────────────────────────────────────────
  bindWildModal() {
    this.wildModal = this.querySelector('[data-bcu-wild-modal]');
    if (!this.wildModal) return;
    this.querySelectorAll('[data-bcu-wild-close]').forEach((el) => {
      el.addEventListener('click', () => this.closeWildModal());
    });
    const add = this.querySelector('[data-bcu-wild-add]');
    if (add) {
      add.addEventListener('click', () => {
        this.closeWildModal();
        const variantId = add.dataset.variantId;
        const title = add.dataset.title || 'Wild Card';

        // Carry exclusions the reader already set elsewhere in the flow — the
        // quiz, a product page — but never interrupt the add to ask for them.
        // A modal on top of an add-to-cart is friction on the one click that
        // matters, and the Wild Card adds perfectly well without it. Anyone who
        // wants to set exclusions can still do it on the product page.
        const prefs = window.BlindlyReadingPrefs
          && window.BlindlyReadingPrefs.getAny
          && window.BlindlyReadingPrefs.getAny();
        const carry = prefs && (prefs.genres.length || prefs.themes.length) ? prefs : null;
        this.addBook(variantId, title, null, add, carry);
      });
    }
  }

  openWildModal() { if (this.wildModal) { this.wildModal.hidden = false; this.adaOpen(this.wildModal); } }
  closeWildModal() { if (this.wildModal && !this.wildModal.hidden) { this.wildModal.hidden = true; this.adaClose(); } }

  // ── ADA: focus into the open modal, Esc closes it (without also closing
  // the cart drawer), focus returns to the opener on close ─────────────
  adaOpen(modal) {
    this.adaLastFocus = document.activeElement;
    if (!modal.dataset.adaBound) {
      modal.dataset.adaBound = '1';
      modal.addEventListener('keyup', (e) => {
        if (e.key !== 'Escape') return;
        e.stopPropagation();
        if (modal === this.modal) this.closeFlavorModal(); else this.closeWildModal();
      });
      modal.addEventListener('keydown', (e) => {
        if (e.key !== 'Tab') return;
        const f = Array.prototype.filter.call(modal.querySelectorAll('button:not([disabled]), [href], input:not([disabled])'), (el) => el.offsetParent !== null);
        if (!f.length) return;
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      });
    }
    setTimeout(() => { const x = modal.querySelector('.bcu__modal-x'); if (x) x.focus(); }, 30);
  }
  adaClose() {
    const t = this.adaLastFocus; this.adaLastFocus = null;
    if (t && document.contains(t) && typeof t.focus === 'function') setTimeout(() => t.focus(), 30);
  }

  // ── Flavor picker modal ─────────────────────────────────────────
  bindFlavorModal() {
    this.modal = this.querySelector('[data-bcu-flavor-modal]');
    if (!this.modal) return;
    this.modalList = this.querySelector('[data-bcu-flavor-list]');
    this.modalTitle = this.querySelector('[data-bcu-flavor-title]');
    this.modalAdd = this.querySelector('[data-bcu-flavor-add]');

    this.querySelectorAll('[data-bcu-flavor-close]').forEach((el) => {
      el.addEventListener('click', () => this.closeFlavorModal());
    });
    this.modalAdd.addEventListener('click', () => {
      if (!this.pending || !this.pending.flavor) return;
      const p = this.pending;
      this.closeFlavorModal();
      this.addBook(p.variantId, p.title, p.flavor, p.btn);
    });
  }

  openFlavorModal(variantId, title, flavors, btn) {
    this.pending = { variantId, title, flavor: null, btn };
    this.modalTitle.textContent = title;
    this.modalList.innerHTML = '';
    flavors.forEach((f) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'bcu__flavor-btn';
      b.textContent = f;
      b.setAttribute('aria-pressed', 'false');
      b.addEventListener('click', () => {
        this.pending.flavor = f;
        this.modalList.querySelectorAll('.bcu__flavor-btn').forEach((el) => { el.classList.remove('is-selected'); el.setAttribute('aria-pressed', 'false'); });
        b.classList.add('is-selected');
        b.setAttribute('aria-pressed', 'true');
        this.modalAdd.disabled = false;
      });
      this.modalList.appendChild(b);
    });
    this.modalAdd.disabled = true;
    this.modal.hidden = false;
    this.adaOpen(this.modal);
  }

  closeFlavorModal() {
    const wasOpen = this.modal && !this.modal.hidden;
    if (this.modal) this.modal.hidden = true;
    this.pending = null;
    if (wasOpen) this.adaClose();
  }

  // ── Carousel arrows ─────────────────────────────────────────────
  bindArrows() {
    const track = this.querySelector('[data-bcu-track]');
    if (!track) return;
    const prev = this.querySelector('[data-bcu-prev]');
    const next = this.querySelector('[data-bcu-next]');
    const scrollByCard = (dir) => {
      const card = track.querySelector('.bcu__card');
      const step = card ? card.getBoundingClientRect().width + 12 : 240;
      track.scrollBy({ left: step * dir, behavior: 'smooth' });
    };
    // Hide prev at the start, next at the end — a lone arrow reads as clutter.
    const update = () => {
      const max = track.scrollWidth - track.clientWidth - 2;
      if (prev) prev.hidden = track.scrollLeft <= 2;
      if (next) next.hidden = track.scrollLeft >= max;
    };
    if (prev) prev.addEventListener('click', () => scrollByCard(-1));
    if (next) next.addEventListener('click', () => scrollByCard(1));
    track.addEventListener('scroll', update, { passive: true });
    update();
  }
}

if (!customElements.get('blindly-cart-upsell')) {
  customElements.define('blindly-cart-upsell', BlindlyCartUpsell);
}
